"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { ArrowRight, Loader, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center h-full space-y-6 py-12 animate-in fade-in duration-500">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          <ArrowRight className="size-6 -rotate-45" />
        </div>
        <h3 className="text-2xl font-default text-foreground">Thank You</h3>
        <p className="text-muted-foreground font-serif text-xl">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary hover:bg-accent mt-4 font-default uppercase tracking-widest">
          Send Another Request
        </Button>
      </div>;
  }
  const inputStyles = "border-2 border-border rounded-none shadow-none px-4 py-3 bg-white focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground font-serif md:text-xl h-auto";
  const labelStyles = "text-[10px] sm:text-xs uppercase tracking-widest text-foreground font-default font-bold mb-1";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className={inputStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground font-default">
                Please provide your full name for our records.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground font-default">
                We will use this to send your free quote.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-()]{7,15}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(941) 888-0485" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="brand_name" rules={{
        required: "Address is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Service Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Punta Gorda Way" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_type" rules={{
      required: "Service type is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelStyles}>Service Required</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`w-full appearance-none outline-none ${inputStyles} cursor-pointer`} {...field}>
                  <option value="" disabled>
                    Select a service...
                  </option>
                  {["General Pest Control", "Termite Treatment", "Mosquito Control", "Spider Control", "Free Inspection"].map((option, i) => <option key={option} value={option} data-index={i}>
                      {option}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Details are required"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelStyles}>How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your pest problem or specific concerns..." className={`${inputStyles} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground font-default">
              The more detail you provide, the better we can prepare for your inspection.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border-2 border-destructive text-destructive text-sm font-default font-bold">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm uppercase tracking-widest font-bold transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              Get A Free Quote
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function LetsBuildSomethingTogether() {
  return <section data-section-id="223842" id="reach-our-team" className="w-full bg-background text-foreground ![padding:30px_0px_96px_0px]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5 flex flex-col space-y-12">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground">
                Get Your Free Pest Control Inspection Today
              </h1>
              <p className="text-xl text-foreground font-serif max-w-md leading-relaxed">
                Ready to clear your home of unwanted guests? Contact Peace River Pest Control today for reliable, local service in Punta Gorda and surrounding areas.
              </p>
            </div>

            <div className="space-y-8 pt-8 border-t-2 border-foreground">
              <div>
                <h4 className="text-xs uppercase tracking-widest text-foreground font-default font-bold mb-3">
                  Our Office
                </h4>
                <p className="text-2xl font-default font-bold text-foreground">Peace River Pest Control</p>
                <p className="text-foreground font-serif text-xl mt-1">Punta Gorda, FL 33955</p>
                <p className="text-foreground font-serif text-xl mt-2">(941) 888-0485</p>
                
                
                <div className="flex flex-wrap gap-3 mt-6">
                  <div className="bg-secondary text-secondary-foreground px-4 py-1.5 text-[10px] font-default font-bold uppercase tracking-widest">
                    FDACS Licensed
                  </div>
                  <div className="bg-secondary text-secondary-foreground px-4 py-1.5 text-[10px] font-default font-bold uppercase tracking-widest">
                    24/7 Emergency Service available
                  </div>
                </div>
              </div>

              <div className="relative w-full aspect-video sm:aspect-[4/3] bg-white overflow-hidden group border-2 border-foreground">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_regional_service_area.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_regional_service_area.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_regional_service_area.png-wvc-sizes" alt="Peace River Service Area Map" loading="lazy" className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 border border-foreground/20 pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                  <div className="size-4 bg-primary rounded-lg animate-pulse border-2 border-foreground" />
                </div>
              </div>
              
              <div className="pt-4">
                <Button asChild className="w-full h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm uppercase tracking-widest font-bold transition-all">
                  <Link to="tel:9418880485">Call Us Now</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8 xl:pl-16">
            <div className="mb-8">
              <h3 className="text-2xl font-default font-bold text-foreground uppercase tracking-tight">Schedule Your Free Inspection</h3>
            </div>
            <FormProvider formKey="reach_team_form_contact" formId="reach_team_form_contact" sectionName="ReachOurTeam" defaultValues={{
            name: "",
            email: "",
            phone: "",
            brand_name: "",
            project_type: "General Pest Control",
            project_description: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            brand_name: {
              required: true
            },
            project_type: {
              required: true
            },
            project_description: {
              required: true
            }
          }} submitText="Get A Free Quote" successMessage="Thank you for reaching out! A Peace River specialist will contact you shortly to schedule your inspection." className="w-full">
              <CollaborationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}